import React from 'react';

const OpponentProcessTheory = props =>
    <div>
        <h2>The hidden benefits of pain: Opponent Process Theory</h2>
        <time dateTime="2018-05-28">Ryan Henao - Monday, May 28, 2018</time>
        <p>
            Let's try a fun little experiment. Stare at <em><a href="/images/star.png">this</a></em> for 30 seconds then look off to the white
            part of the screen. What do you see?
        </p>
        <p> 
            Opponent-process theory, developed by German physiologist
            Ewald Hering, is a psychological and neurological model used for
            explaining aspects of visual perception.  Hering noticed that
            certain colors never occur together. You wouldn't see a
            "greenish red" or a "yellowish blue". He proposed that certain
            pairs of colors oppose each other and that once a stimulus of one
            color is presented, the opponent color is perceived after the
            stimulus is removed. This happens because color A is perceived by
            an excited response and color B by an inhibitory response. Once you
            remove color A, the chemical reaction in your eyes slows down. From
            this slowing, the inhibitory response is kicked in and you "see"
            the opposite color B.
        </p>
        <p>
            So great. This is interesting, but besides a fancy name I can use
            to impress my friends, what use is this theory? It turns out,
            as American psychologist Richard Solomon later discovered, opponent
            process theory not only explains how our eyes work but also a vast
            array of emotional and behavioral phenomena. It explains why we
            become addicted, why we get depressed, and why most people are
            going after pleasure and avoiding pain in a completely backward
            way. So what did Solomon find?
        </p>
        <p>
            In 1980 Solomon published a paper titled "The Opponent-Process
            Theory of Acquired Motivation: The Costs of Pleasure and Benefits
            of Pain". In his paper, he shows why pain can sometimes be
            beneficial.
        </p>
        <h4>Hedonic Contrast</h4>
        <p>
            Solomon begins by observing what he calls "hedonic contrast". Similar
            to its color theory equivalent, hedonic contrast states that for every
            positive experience A, on the removal of the stimulus there is a
            negative experience B. This was shown in an experiment on ducklings.
        </p>
        <blockquote>
            A moving mother duck is presented to a 5-hour-old duckling for
            the first time in its life. The duckling becomes excited,
            stumbles in the general direction of the mother, and moves its
            head quickly so that the mother is kept in sight. After 1
            minute elapses, the mother is removed. Then, with a latency of
            5-10 seconds, the duckling shows agitated head movements and
            energetic, seemingly random locomotor activity and emits a
            repetitive, high-pitched sound referred to as distress
            calling... Therefore, one can infer that the presentation of
            the reinforcer engenders a pleasant or desirable hedonic state
            but that the termination of the reinforcer results in an
            aversive or unpleasant hedonic state"
        </blockquote> 
        <h4>Hedonic Adaptation</h4>
        <p>
            The next phenomena Solomon observes he calls hedonic adaptation.
            Hedonic adaptation or tolerance, as you may know, is what happens
            when a person becomes habituated to an experience. He uses opiate
            addiction as an example. The first dose of an opiate provides a
            huge euphoric rush followed later by a state of psychological and
            physical discomfort: withdrawal.  
        </p>
        <blockquote> 
            When the drug
            dose loses its effect because of metabolic destruction, the
            user goes into a state of mild discomfort with both
            physiological and psychological aspects. The physiological
            aspect includes runny eyes and nose, abdominal pains, clammy
            skin, and muscular malaise. The psychological aspect is called
            craving and refers to an aversive state. Most organisms will
            perform an operant if it will get rid of an aversive state.
            Thus, drug users tend to redose because this is the surest and
            quickest way to get rid of the physiological and psychological
            aspects of withdrawal aversiveness.  A slower way is merely to
            let time go by because the withdrawal aversiveness will slowly
            die away; but this method is less preferred.
        </blockquote>
        <p>
            After redosing repeatedly, the rush begins to diminish while the
            withdrawal grows in intensity and duration.  This drives the user
            to take higher doses and more frequently; however, this only speeds
            the habituation process and eventually the experience is completely
            reversed. The user experiences a small amount of pleasure followed
            by a large amount of pain. Since the opponent process is more
            sluggish, the pain lasts much longer. 
            <em><a href="/documents/OPT-graphs.png">Here's</a></em> an outline of this
            process.
        </p>
        <p>
            The opponent process also tends to stay stronger even when
            abstaining from the activity for long periods of time.  Solomon
            calls this the "savings effect".
        </p>
        <blockquote>
            In alcohol addiction, for example, the abstainer is warned that
            one drink may be disastrous, and the reason is the savings
            principle. The reexercise of alcohol’s opponent-process system
            strengthens the withdrawal syndrome very rapidly and sets up
            the special conditions for resumption of the addictive cycle.
        </blockquote>
        <h4>Addictions</h4>
        <p>
            This explains why our addictions are so hard to break. As
            you repeat the pleasant experience, you get less pleasure from it
            and grow more uneasy. The tolerance and increased uneasiness drive you to
            relieve it by again indulging in more of the behavior. You get caught in a
            downward spiraling loop staying in a constant low-level state of
            dissatisfaction. 
        </p>  
        <p>
            It's obvious how opponent process theory explains the process of
            hard addictions such as drug addiction, gambling, alcoholism, porn
            et cetera, but what may not be so obvious are subtle everyday
            addictions, which can be just as debilitating.  As Solomon
            demonstrates through various experiments outlined in his paper, ANY
            pleasurable behavior that can be exercised repeatedly 
            grows the opponent process. Social Media, Netflix,
            stress eating, codependent relationships, watching the news, gaming, and
            shopping are some examples of things which potentially turn into   
            sources of pain. 
        </p>
        <p> 
            Any experience that can be broken down into
            sugary bite-size pieces and quickly consumed over and over falls
            under what I call the "emotional spike pattern". This is similar to
            what happens when you eat large amounts of sugar. When you eat
            sugary empty calorie foods you experience a sugar spike followed by
            a crash. Do this frequently enough and you build insulin resistance
            which can lead to type 2 diabetes. Too much sugar is a bad thing.
        </p>
        <blockquote>
            We have been taught to think of aversion and
            trauma as the only affective sources of physiological
            stress. The opponent-process model implies that
            often repeated pleasures are just as fertile a source
            of physiological stress. 
        </blockquote>
        <p>
            The problem in modern society is that when it comes to avoiding
            overindulgence, sugar is everywhere. Social media
            platforms such as Reddit, twitter, and facebook, bombard you with a
            never-ending stream addicting superficial distractions. Popular
            news sites such as CNN and Fox are more entertainment and drama
            than actual news.  Youtube quickly learns what you like to watch,
            creates suggestions and automatically plays them to keep you on.
            Netflix is churning out original series every week. There's always
            a new show to get hooked on. To top it all off, all of these
            distractions are available any time of day from the small powerful
            device you carry around in your pocket. 
        </p>
        <p> 
            Every platform is optimized to keep you on it as long as
            possible—at the expense of your happiness. It's no wonder then that
            we struggle with this. It's not some grand conspiracy. These
            companies understand human behavior, which is why they became
            popular in the first place, but it's also why they are addicting.
            Unfortunately, looking at cat memes on Reddit and watching fail
            compilations on youtube is not going to create a deep sense of well
            being. 
        </p>
        <h4>Hedonic reversal</h4>
        <p>
            Fortunately, Solomon shows that the opponent process also works in
            reverse.  When a negative stimulus is introduced, removing the
            stimulus creates a positive experience. With repetition, the
            initial negative experience weakens and the positive grows
            stronger.  He observes this phenomenom in skydivers: 
        </p>
        <blockquote>
            During the first free-fall (before the parachute opens),
            even the bravest men show a fearful reaction.
            Their eyes may bulge, their lips retract, they
            may yell, their bodies curl forward, and their
            autonomic nervous systems are in a high state of
            excitation. There may even be involuntary urination.
            After they have landed safely, they go
            through a short period during which they look
            stunned and stony faced and do not talk much.
            Then, after a few minutes, they begin to chatter
            and enter into lively social interaction with peers.
            The after-reaction appears to last about 10 minutes.
            After many parachute jumps, the signs of affective
            habituation are clear, and the fearful reaction
            is usually undetectable. Instead, the parachutists
            look tense, eager, or excited, and during the free-
            , fall they report a "thrill." 
        </blockquote>
        <p>
            With the experienced parachutists this "high" can last
            for hours. 
        </p>
        <h4>Turning pain into pleasure</h4>
        <p>    
        So now that you understand the opponent process, how can you turn pain
        into pleasure?  The answer is to seek activities that indirectly lead
        to pleasure. Things which are uncomfortable at first but you can work
        to skillfully improve them over time. Some examples are jogging, weight
        lifting, intermittent fasting, cold showers, meditating, eating
        healthy, public speaking, and meeting new people. Anything that
        challenges you to expand and grow as a person. 
        </p> 
        <p>
        When facing these challenges, the difficulty usually lies in making it
        past the uncomfortable stage in the beginning. This is the stage where
        you're blindly groping around in the dark, desperate for direction. The
        overwhelming confusion and pain drive most people to give up.  You buy
        a new book and only read the first chapter or sign up for a new gym
        membership to only go for a few weeks. You get overwhelmed by the new
        activity and how little you know. Or maybe you feel doubt about whether
        it's even a worthwhile endeavor. Discourage sets in when you realize
        how high the climb to mastery is. What is that you say? It takes 10 to
        15 years to earn a black belt in Jiu-Jitsu? I think I'll just go back
        to watching Netflix on the couch. You put off the very thing that will
        free you.
        </p>
        <p>
        You must avoid the tendency numb and distract in the face of stressful stimulus.
        Instead, when learning something new be open like a sponge absorbing
        everything. Embrace the fact that you aren't going to be a pro
        overnight. Focus on every tiny victory. Set the bar low and slowly work
        your way up. As you challenge yourself more by leaning into uncomfortable
        situations, you will become comfortable with being uncomfortable and as
        you become better at this process you are able to more easily move past
        the initial pain barrier. You will soon establish a rhythm and slowly
        begin to enjoy the process.
        </p>
        <p>
        I'll give you an example from my personal life. When I first started
        meditating I was terrible at it. Growing up, I was the type of person
        who spaced out all the time and always distracted. I couldn't even sit
        for more than 5 minutes. Most of that time was spent just sitting there
        in a daydream unable to center my mind and focus. My knees would get
        unbearably stiff and sore from sitting. I noticed zero benefits. In
        short—it was painful. However, I continued to patiently work at it not
        missing a day and after a few months I had worked myself up to 20
        minutes per daily session. I started to get the hang of it and noticed
        how much calmer and focused I was after sitting. 
        </p>
        <p> 
        After 3 years, I now sit for an hour every day and after each sit,
        I have a calm energetic buzz that lasts for hours and sometimes even
        the entire day. It's similar to the runner's high joggers feel or the
        state of flow a master musician experiences.  These things take time to
        build up and are long-term investments.  It's hard in a culture that's
        obsessed with quick results, but you need to think on a scale of months
        to years instead of trying to transform your life in ten days.  
        </p> 
        <h4>Questions to ask yourself</h4>
        <ul>
            <li>
                What skillful activities can I engage in to improve the quality of my life?
            </li>
            <li>
                If you're drawing a blank, what are some new habits, hobbies, or challenges I'm interested in diving into?
            </li>
            <li>
                What are my avoidance behaviors and how can I cut back on them? 
            </li>
        </ul>
        <h4>Links</h4>
        <ul className="link-group">
            <li><a href="/documents/Solomon-Opponent-Process-1980.pdf">The Opponent-Process Theory of Acquired Motivation PDF</a></li>
            <li><a href="https://en.wikipedia.org/wiki/Opponent-process_theory">Opponent-process theory Wikipedia</a></li>
            <li><a href="http://www.psywww.com/intropsych/ch14_frontiers/solomons_opponent_process_theory.html">Solomon's "Opponent Process" Theory</a></li>
            <li><a href="https://gettingstronger.org/2010/05/opponent-process-theory/">Opponent-process</a></li>
        </ul>
    </div>
export default OpponentProcessTheory;
